/**
 * Event Sessions Service
 * Handles event session-related database operations
 */

import { supabase } from './supabaseClient';
import { Database } from '@/types/supabase';
import { NotFoundError, DatabaseError } from '@/lib/errors';
import {
  EventSession,
  EventSessionInsert,
  EventSessionUpdate,
} from '@/types/event.types';

// Type helpers for database operations
type EventSessionRow = Database['public']['Tables']['event_sessions']['Row'];

/**
 * Get all sessions for an event
 * @param eventId - Event ID
 * @throws {DatabaseError} If the database query fails
 */
export async function getEventSessions(
  eventId: string,
): Promise<EventSession[]> {
  try {
    const { data, error } = await supabase
      .from('event_sessions')
      .select('*')
      .eq('event_id', eventId)
      .order('display_order', { ascending: true });

    if (error) throw error;
    return (data as EventSessionRow[]) || [];
  } catch (error) {
    throw new DatabaseError(
      `Failed to fetch sessions for event ID ${eventId}`,
      error,
    );
  }
}

/**
 * Get a single session by ID
 * @throws {NotFoundError} If session is not found
 * @throws {DatabaseError} If the database query fails
 */
export async function getSessionById(id: string): Promise<EventSession | null> {
  try {
    const { data, error } = await supabase
      .from('event_sessions')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        throw new NotFoundError(`Session with ID ${id} not found`);
      }
      throw error;
    }

    return data as EventSession;
  } catch (error) {
    if (error instanceof NotFoundError) throw error;
    throw new DatabaseError(`Failed to fetch session with ID ${id}`, error);
  }
}

/**
 * Create a new event session
 * @throws {DatabaseError} If the database operation fails
 */
export async function createEventSession(
  session: EventSessionInsert,
): Promise<EventSession> {
  try {
    // If no display_order provided, calculate next order
    let finalSession = session;
    if (session.display_order === undefined || session.display_order === null) {
      const { data: existingSessions, error: countError } = await supabase
        .from('event_sessions')
        .select('display_order')
        .eq('event_id', session.event_id)
        .order('display_order', { ascending: false })
        .limit(1);

      if (countError) throw countError;

      const maxOrder =
        existingSessions && existingSessions.length > 0
          ? existingSessions[0].display_order
          : 0;

      finalSession = { ...session, display_order: (maxOrder || 0) + 1 };
    }

    const { data, error } = await supabase
      .from('event_sessions')
      .insert(finalSession as never)
      .select()
      .single();

    if (error) throw error;
    return data as EventSession;
  } catch (error) {
    throw new DatabaseError('Failed to create event session', error);
  }
}

/**
 * Create multiple sessions at once
 * @throws {DatabaseError} If the database operation fails
 */
export async function createEventSessions(
  sessions: EventSessionInsert[],
): Promise<EventSession[]> {
  try {
    // Calculate display_order for sessions without it
    const { data: existingSessions, error: countError } = await supabase
      .from('event_sessions')
      .select('display_order')
      .eq('event_id', sessions[0].event_id)
      .order('display_order', { ascending: false })
      .limit(1);

    if (countError) throw countError;

    let nextOrder =
      existingSessions && existingSessions.length > 0
        ? (existingSessions[0].display_order || 0) + 1
        : 1;

    const finalSessions = sessions.map((session) => {
      if (
        session.display_order === undefined ||
        session.display_order === null
      ) {
        const withOrder = { ...session, display_order: nextOrder };
        nextOrder++;
        return withOrder;
      }
      return session;
    });

    const { data, error } = await supabase
      .from('event_sessions')
      .insert(finalSessions as never)
      .select();

    if (error) throw error;
    return (data as EventSessionRow[]) || [];
  } catch (error) {
    throw new DatabaseError('Failed to create event sessions', error);
  }
}

/**
 * Update an existing session
 * @throws {NotFoundError} If session is not found
 * @throws {DatabaseError} If the database operation fails
 */
export async function updateEventSession(
  id: string,
  updates: EventSessionUpdate,
): Promise<EventSession> {
  try {
    const { data, error } = await supabase
      .from('event_sessions')
      .update({ ...updates, updated_at: new Date().toISOString() } as never)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        throw new NotFoundError(`Session with ID ${id} not found`);
      }
      throw error;
    }

    return data as EventSession;
  } catch (error) {
    if (error instanceof NotFoundError) throw error;
    throw new DatabaseError(`Failed to update session with ID ${id}`, error);
  }
}

/**
 * Delete a session
 * @throws {DatabaseError} If the database operation fails
 */
export async function deleteEventSession(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('event_sessions')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    throw new DatabaseError(`Failed to delete session with ID ${id}`, error);
  }
}

/**
 * Reorder sessions for an event
 * @param eventId - Event ID
 * @param sessionOrders - Array of { id, display_order } objects
 * @throws {DatabaseError} If the database operation fails
 */
export async function reorderEventSessions(
  eventId: string,
  sessionOrders: { id: string; display_order: number }[],
): Promise<EventSession[]> {
  try {
    // Update each session's display_order
    const updatePromises = sessionOrders.map(({ id, display_order }) =>
      supabase
        .from('event_sessions')
        .update({
          display_order,
          updated_at: new Date().toISOString(),
        } as never)
        .eq('id', id),
    );

    const results = await Promise.all(updatePromises);

    // Check for errors
    const errors = results.filter((r) => r.error);
    if (errors.length > 0) {
      throw new Error(`Failed to update ${errors.length} sessions`);
    }

    // Fetch updated sessions
    return await getEventSessions(eventId);
  } catch (error) {
    throw new DatabaseError(
      `Failed to reorder sessions for event ID ${eventId}`,
      error,
    );
  }
}

/**
 * Duplicate a session (copy all fields except id and timestamps)
 * @param sessionId - Session ID to duplicate
 * @param userId - User performing the duplication
 * @throws {NotFoundError} If source session is not found
 * @throws {DatabaseError} If the database operation fails
 */
export async function duplicateEventSession(
  sessionId: string,
  userId: string,
): Promise<EventSession> {
  try {
    // Get source session
    const source = await getSessionById(sessionId);
    if (!source) {
      throw new NotFoundError(`Session with ID ${sessionId} not found`);
    }

    // Create duplicate with new data
    const duplicate: EventSessionInsert = {
      event_id: source.event_id,
      name: `${source.name} (Copia)`,
      description: source.description,
      session_date: source.session_date,
      start_time: source.start_time,
      end_time: source.end_time,
      created_by: userId,
      // display_order will be auto-calculated
    };

    return await createEventSession(duplicate);
  } catch (error) {
    if (error instanceof NotFoundError) throw error;
    throw new DatabaseError(
      `Failed to duplicate session with ID ${sessionId}`,
      error,
    );
  }
}

/**
 * Get sessions by date
 * @param eventId - Event ID
 * @param date - Date to filter by (ISO format)
 * @throws {DatabaseError} If the database query fails
 */
export async function getSessionsByDate(
  eventId: string,
  date: string,
): Promise<EventSession[]> {
  try {
    const { data, error } = await supabase
      .from('event_sessions')
      .select('*')
      .eq('event_id', eventId)
      .eq('session_date', date)
      .order('start_time', { ascending: true });

    if (error) throw error;
    return (data as EventSessionRow[]) || [];
  } catch (error) {
    throw new DatabaseError(
      `Failed to fetch sessions for event ID ${eventId} on date ${date}`,
      error,
    );
  }
}

/**
 * Get count of sessions for an event
 * @param eventId - Event ID
 * @throws {DatabaseError} If the database query fails
 */
export async function getSessionCount(eventId: string): Promise<number> {
  try {
    const { count, error } = await supabase
      .from('event_sessions')
      .select('*', { count: 'exact', head: true })
      .eq('event_id', eventId);

    if (error) throw error;
    return count || 0;
  } catch (error) {
    throw new DatabaseError(
      `Failed to count sessions for event ID ${eventId}`,
      error,
    );
  }
}
